import React, { useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import AppTitle from '@/components/common/title';
import s from './styles.module.scss';
import classNames from 'classnames';
import AppCarusel, { IAppCarouselRef } from '@/components/common/carousel';
import { Fade, IconButton } from '@mui/material';
import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';
import { ReadBookmark } from '@/infrastructure/class/read-bookmark';
import ReadBookmarksCarouselItem from './item';
import { SwiperSlide } from 'swiper/react';

interface IReadBookmarksCarouselProps {
  items: ReadBookmark[];
  className?: string;
  title?: string;
}

const ReadBookmarksCarousel: React.FC<IReadBookmarksCarouselProps> = (
  props
) => {
  const { t: tButtons } = useTranslation('buttons');
  const { t: tCommon } = useTranslation('common');

  const { items, className, title = tCommon('reading') } = props;

  const hasItems = !!items.length;

  const [showFull, setShowFull] = useState<boolean>(false);

  const carouselRef = useRef<IAppCarouselRef>(null);

  const wrapperClassNames = classNames(s['carousel-wrapper'], className);
  const noItemsClassNames = classNames(s['no-item'], {
    [s['has-items']]: hasItems,
  });

  const toggleViewer = () => {
    setShowFull(!showFull);
  };

  const onPrev = () => {
    if (carouselRef.current) {
      carouselRef.current.prevSlide();
    }
  };
  const onNext = () => {
    if (carouselRef.current) {
      carouselRef.current.nextSlide();
    }
  };

  return (
    <div className={wrapperClassNames}>
      <div className={s['header']}>
        <AppTitle className={s['title']} title={title} />
        <div className={s['sub-title']}>
          {items.length > 0 && (
            <button className={s['link']} onClick={toggleViewer}>
              {showFull ? tButtons('seeShort') : tButtons('seeAll')}
            </button>
          )}
        </div>
      </div>
      <div className={s['body']}>
        {showFull ? (
          <Fade in={hasItems} timeout={1000} unmountOnExit>
            <div className={s['list']}>
              {items.map((readSurah, index) => (
                <ReadBookmarksCarouselItem
                  key={`${readSurah.ayaIndex}-${readSurah.suraIndex}-${index}`}
                  data={readSurah}
                />
              ))}
            </div>
          </Fade>
        ) : (
          <Fade in={hasItems} timeout={1000} unmountOnExit>
            <div className={s['controls']}>
              <IconButton className={s['btn']} onClick={onPrev}>
                <ChevronLeftRounded />
              </IconButton>
              <AppCarusel
                className={s['carousel']}
                ref={carouselRef}
                loop={false}
                spaceBetween={20}
              >
                {items.map((readSurah, index) => (
                  <SwiperSlide
                    className={s['slide']}
                    key={`${readSurah.ayaIndex}-${readSurah.suraIndex}-${index}`}
                  >
                    <ReadBookmarksCarouselItem data={readSurah} />
                  </SwiperSlide>
                ))}
              </AppCarusel>
              <IconButton className={s['btn']} onClick={onNext}>
                <ChevronRightRounded />
              </IconButton>
            </div>
          </Fade>
        )}
        <div className={noItemsClassNames}>
          <AppTitle title={tCommon('noItems')} />
        </div>
      </div>
    </div>
  );
};

export default ReadBookmarksCarousel;
